import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import type { Options } from 'react-select';
import { graphql } from '../../gql';
import {
  type DropdownOption,
  transformSenderReceiverCountryOption,
} from '../utils/optionTransformers';
import type { SenderReceiverCountry } from './fragments/gql/SenderReceiverCountry';
import type { QueryResult } from './types/QueryResult';

export const senderReceiverCountryQuery = graphql(/* GraphQL */ `
  query SenderReceiverCountryQuery {
    viewer {
      senderReceiverCountries {
        items {
          ...SenderReceiverCountry
        }
      }
    }
  }
`);

export const transformCountryOptions = (
  data: SenderReceiverCountry[] | undefined,
  includeInvalidCountries = false,
): Options<DropdownOption<SenderReceiverCountry>> => {
  if (!data) {
    return [];
  }

  return data
    .filter(c => includeInvalidCountries || c.isValid)
    .map(transformSenderReceiverCountryOption);
};

export const useSenderReceiverCountryOptions = (options?: {
  includeInvalidCountries: boolean;
}): QueryResult<Options<DropdownOption<SenderReceiverCountry>>> => {
  const { data, loading, error } = useQuery(senderReceiverCountryQuery, {
    fetchPolicy: 'cache-first',
  });

  const countryOptions = useMemo(
    () =>
      transformCountryOptions(
        data?.viewer.senderReceiverCountries.items,
        options?.includeInvalidCountries,
      ),
    [data?.viewer.senderReceiverCountries.items, options?.includeInvalidCountries],
  );

  return [countryOptions, loading, error];
};
